import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CartCrossSell extends HTMLElement {
  connectedCallback() {
    this.#selectRandomCandidate();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('[data-cart-cross-sell-swatch]');
    if (swatch instanceof HTMLButtonElement && this.contains(swatch)) {
      this.#selectSwatch(swatch);
      return;
    }

    const button = event.target.closest('[data-cart-cross-sell-add]');
    if (button instanceof HTMLButtonElement && this.contains(button)) {
      this.#addVariant(button);
    }
  };

  #selectRandomCandidate() {
    const candidates = Array.from(this.querySelectorAll('[data-cart-cross-sell-candidate]')).filter(
      (candidate) => candidate instanceof HTMLElement
    );

    if (candidates.length === 0) return;

    const selectedIndex = Math.floor(Math.random() * candidates.length);
    candidates.forEach((candidate, index) => {
      candidate.hidden = index !== selectedIndex;
    });
  }

  /**
   * @param {HTMLButtonElement} swatch
   */
  #selectSwatch(swatch) {
    const item = swatch.closest('[data-cart-cross-sell-item]');
    if (!(item instanceof HTMLElement)) return;

    const addButton = item.querySelector('[data-cart-cross-sell-add]');
    const price = item.querySelector('[data-cart-cross-sell-price]');
    const shade = item.querySelector('[data-cart-cross-sell-shade]');

    if (addButton instanceof HTMLButtonElement && swatch.dataset.variantId) {
      addButton.dataset.variantId = swatch.dataset.variantId;
    }

    if (price && swatch.dataset.priceHtml) {
      price.innerHTML = swatch.dataset.priceHtml;
    }

    if (shade instanceof HTMLElement && swatch.dataset.variantTitle) {
      shade.textContent = `Shade: ${swatch.dataset.variantTitle}`;
    }

    item.querySelectorAll('[data-cart-cross-sell-swatch]').forEach((button) => {
      const isSelected = button === swatch;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #addVariant(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const originalText = button.textContent || 'Add';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('sections', this.#getCartSectionIds().join(','));
      formData.append('sections_url', window.location.pathname);

      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'text/html',
        },
      });

      const data = await response.json();

      if (!response.ok || data?.status) {
        this.#showError(data?.description || data?.message || 'Could not add this item. Please try again.');
        this.#resetButton(button, originalText);
        return;
      }

      const cart = await this.#fetchCart();
      const itemCount = cart?.item_count ?? this.#getCurrentCartCount() + 1;

      this.dispatchEvent(
        new CartAddEvent(cart ?? data, 'cart-cross-sell', {
          source: 'cart-cross-sell',
          itemCount,
          variantId,
          sections: data.sections,
        })
      );

      this.#resetDrawerScroll();
    } catch (error) {
      console.error('Cart cross-sell add error:', error);
      this.#showError('Could not add this item. Please try again.');
      this.#resetButton(button, originalText);
    }
  }

  /**
   * @returns {string[]}
   */
  #getCartSectionIds() {
    const sectionIds = [];

    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    }

    return [...new Set(sectionIds)];
  }

  /**
   * @returns {Promise<any | null>}
   */
  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * @returns {number}
   */
  #getCurrentCartCount() {
    const count = document.querySelector('[data-testid="cart-icon"] .cart-bubble__text-count')?.textContent?.trim();
    const parsedCount = Number.parseInt(count ?? '0', 10);
    return Number.isFinite(parsedCount) ? parsedCount : 0;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} text
   */
  #resetButton(button, text) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = text;
  }

  #resetDrawerScroll() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        for (const selector of ['.cart-drawer__content', '.cart-drawer__items']) {
          const element = document.querySelector(selector);
          if (element instanceof HTMLElement) {
            element.scrollTo({ top: 0, left: 0 });
          }
        }
      });
    });
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    let error = this.querySelector('.cart-cross-sell__error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'cart-cross-sell__error';
      error.setAttribute('role', 'alert');
      this.querySelector('.cart-cross-sell__inner')?.append(error);
    }

    error.textContent = message;
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
